import axios, { AxiosError } from 'axios';
import { IAvatarAssets } from '@interfaces/avatar';

import {
  GenderType,
  IAvaturnAvatarStatus,
  IAvaturnData,
  IAvaturnUpdate,
  IExportAvaturn,
  INewAvaturnData,
  SkinToneType,
} from '../interfaces';

import { env } from 'env';

const { REACT_APP_API_URL } = env;

const customAxios = axios.create();

export default class AvaturnService {
  static createSession(
    token: string,
    avaturnId: string,
    avatarId: string,
    sessionType: string,
    avaturnApiUrl: string,
  ) {
    if (!avaturnId) {
      console.log('Avaturn: createSession Missing required parameters');
      return null;
    }

    let config = { type: sessionType } as any;
    if (sessionType === 'edit_existing') {
      config = { ...config, avatar_id: avatarId };
    }

    return customAxios
      .post(
        `${avaturnApiUrl}sessions/new`,
        {
          user_id: avaturnId,
          config,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => res.data)
      .catch((error) => {
        console.log(error);
        if (error.response?.status === 404) {
          return null;
        }
      });
  }

  static async validateUrl(avatarUrl: string) {
    try {
      const result = await customAxios.get(avatarUrl, {
        timeout: 500,
      });

      if (result.status === 200) {
        return true;
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 404) {
          return false;
        } else if (
          error.code === AxiosError.ECONNABORTED ||
          error.message.includes('maxContentLength size of')
        ) {
          return true;
        }
      }
    }

    return false;
  }

  static async getAssets({
    gender,
    ...params
  }: {
    gender: string;
    credentialStatus?: string;
    brand?: string;
    persona?: string;
  }): Promise<IAvatarAssets> {
    return axios
      .get(
        `${REACT_APP_API_URL}api/v2/avatar/avaturn/assets/gender/${gender}`,
        {
          params,
        },
      )
      .then((res) => res.data)
      .catch(() => {
        // Return an empty object
        return {
          assetId: '',
          uniforms: [],
          glasses: [],
          shoes: [],
          hair: [],
          default: [],
        } as IAvatarAssets;
      });
  }

  static async exportAvaturnDataToS3(
    brand: string,
    data: Omit<IExportAvaturn, 'brand'>,
  ) {
    try {
      const [avaturnModelS3, exportedAvatar] = await Promise.all([
        customAxios.get(data.url, {
          responseType: 'arraybuffer',
          responseEncoding: 'binary',
        }),
        AvaturnService.exportAvaturnBE(brand, data),
      ]);

      const { uploadUrl, ...restExportedAvatar } = exportedAvatar;

      const blob = new Blob([avaturnModelS3.data], {
        type: 'model/gltf-binary',
      });

      if (uploadUrl) {
        console.log('uploading to s3');
        await customAxios.put(uploadUrl, blob, {
          headers: {
            ContentType: 'model/gltf-binary',
          },
        });
      }

      return restExportedAvatar;
    } catch (error) {
      console.log('EXPORT');
      throw error;
    }
  }

  static async exportAvaturnBE(
    brand: string,
    data: Omit<IExportAvaturn, 'brand'>,
  ): Promise<IAvaturnData> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/exportAvaturnDataToS3`;
    return axios
      .post(url, { ...data, brand })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in exportAvaturnDataToS3:', error);
        throw error; // Re-throw the error for handling upstream
      });
  }

  static async updateAvatar(
    avaturnId: string,
    data: IAvaturnUpdate,
  ): Promise<IAvaturnData> {
    return axios.patch(
      `${env.REACT_APP_API_URL}api/v2/avatar/avaturn/${avaturnId}`,
      data,
    );
  }

  static async createNewAvaturn(
    brand: string,
    data: {
      avaturnId?: string;
      avatarId?: string;
      gender?: GenderType;
      skinTone?: SkinToneType;
      isGeneric?: boolean;
    },
  ): Promise<INewAvaturnData> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/createNewAvaturn`;

    return axios
      .post(url, {
        avatarId: data.avatarId,
        brand: brand,
        gender: data.gender,
        skinTone: data.skinTone,
        isGeneric: data.isGeneric,
      })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in createNewAvaturn:', error);
        throw error;
      });
  }

  static async deleteAvaturn(avaturnId: string): Promise<void> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/deleteAvaturn`;

    return axios
      .post(url, { avaturnId })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in deleteAvaturn:', error);
        throw error;
      });
  }

  static async deleteAvaturnByParticipant(): Promise<void> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/deleteAvaturnByParticipant`;

    return axios
      .post(url)
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in deleteAvaturnByParticipant:', error);
        throw error;
      });
  }

  static async cleanAvaturnResources(avaturnId: string): Promise<void> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/cleanAvaturnResources`;

    return axios
      .post(url, { avaturnId })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in cleanAvaturnResources:', error);
        throw error;
      });
  }

  static async renderAvatar(
    avatarId: string,
    avaturnAccount: string,
  ): Promise<{ id: string }> {
    const url = `${REACT_APP_API_URL}api/v2/avatar/avaturn/render`;

    return axios
      .post(url, { avatarId, avaturnAccount })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Error in render:', error);
        throw error;
      });
  }

  static async getAvatarInfo(avaturnId: string): Promise<IAvaturnData> {
    return axios
      .get(`${REACT_APP_API_URL}api/v2/avatar/avaturn/${avaturnId}/info`)
      .then((res) => res.data)
      .catch((error) => {
        throw error;
      });
  }

  static async getAvatarQrInfo(
    avaturnId: string,
    qrToken: string,
  ): Promise<IAvaturnData> {
    return customAxios
      .get(`${REACT_APP_API_URL}api/v2/qr/avatar/${avaturnId}/info`, {
        headers: {
          Authorization: `Bearer ${qrToken}`,
        },
      })
      .then((res: any) => res.data)
      .catch((error) => {
        throw error;
      });
  }

  static getAvatarStatus(
    avaturnId: string,
    avatarId: string,
  ): Promise<IAvaturnAvatarStatus | null> {
    return axios
      .get(
        `${REACT_APP_API_URL}api/v2/avatar/avaturn/${avaturnId}/avatar/${avatarId}/status`,
      )
      .then((res: any) => res.data)
      .catch((error) => {
        throw error;
      });
  }
}
